import { callApi } from '@/api/callApi';
import { API_ENDPOINT, API_URL_ORDER_SERVICE } from '@/api/constant';
import CommonFilter from '@/components/common/CommonFilter';
import CommonTable, {
  Action,
  TableColumn,
} from '@/components/common/commonTable/CommonTable';
import ExportOption from '@/components/common/export/ExportOption';
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import PageHeading from '@/components/common/PageHeading';
import Pagination from '@/components/common/Pagination';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
// import DateRangePicker from '@/components/UI/DateRange';
import FormField from '@/components/UI/FormField';
import { useListQueryParams } from '@/hooks/useListQueryParams';
import { useSelectableTable } from '@/hooks/useSelectableTable';
import { PageInfo } from '@/interfaces/common.interface';
import {
  ICouponResponseData,
  ICouponResponseList,
} from '@/interfaces/cuponResponse.interface';
import { checkIsExpire, GetFilterUrl } from '@/utility/helper';
import { RoutesLink } from '@/utility/RouteLinks';
import { PlusSquare } from 'lucide-react';
import { useEffect, useState } from 'react';
// import { Range } from 'react-date-range';
import { useNavigate } from 'react-router-dom';

interface CouponStates {
  isLoading: boolean;
  couponStatus: string;
  coupons: ICouponResponseData[];
  pageInfo: PageInfo;
  selectedCoupons: Set<string | number>;
  showDeleteModel: boolean;
  couponToDelete: string | null;
  sortingOption: string;
}

export const couponFilterOption = [
  {
    value: '',
    label: 'All',
  },
  {
    value: 'active',
    label: 'Active',
  },
  {
    value: 'expired',
    label: 'Expired',
  },
  {
    value: 'used',
    label: 'Used',
  },
];

const CouponsList = () => {
  const navigate = useNavigate();
  const { params, setParams } = useListQueryParams();
  const [states, setStates] = useState<CouponStates>({
    isLoading: false,
    couponStatus: '',
    coupons: [],
    pageInfo: {
      total: 0,
      totalPages: 0,
      limit: 10,
      page: 1,
    },
    selectedCoupons: new Set(),
    showDeleteModel: false,
    couponToDelete: null,
    sortingOption: '',
  });

  const handleChangeState = (newState: Partial<typeof states>) => {
    setStates((prev) => ({
      ...prev,
      ...newState,
    }));
  };

  // const [dateRange, setDateRange] = useState<Range[]>([
  //   {
  //     startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  //     endDate: new Date(),
  //     key: 'selection',
  //   },
  // ]);

  const fetchCouponsList = async () => {
    try {
      // const startDate = formatDateTime(dateRange[0].startDate, 'YYMMDD');
      // const endDate = formatDateTime(dateRange[0].endDate, 'YYMMDD');
      const filterQuery = {
        page: params.page,
        limit: params.limit,
        sort: params.sort,
        isActive: params.isActive,
        isExpired: params.isExpired,
        isUsed: params.isUsed,
        search: params.search,
        type: params.discountType,
        target: params.target,
      };

      const query = GetFilterUrl(filterQuery);
      handleChangeState({ isLoading: true });
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.coupons}?${query ? query : ''}`;
      const res = await callApi<ICouponResponseList>(PATH, { method: 'GET' });

      handleChangeState({
        coupons: res.data,
        pageInfo: {
          limit: +res.pageInfo.limit,
          page: +res.pageInfo.page,
          total: +res.pageInfo.total,
          totalPages: +res.pageInfo.totalPages,
        },
        isLoading: false,
      });
    } catch (error) {
      console.log('error is', error);
    }
  };

  const handleDeleteCoupon = async (id: string) => {
    try {
      const PATH = `${API_URL_ORDER_SERVICE}${API_ENDPOINT.coupons}/${id}`;
      await callApi(PATH, { method: 'DELETE' });
    } catch (error) {
      console.log('error is delete coupon', error);
    } finally {
      fetchCouponsList();
    }
  };

  console.log('page inffo', params.page, states.pageInfo);

  useEffect(() => {
    fetchCouponsList();
  }, [
    params.page,
    params.limit,
    params.sort,
    params.isActive,
    params.isExpired,
    params.isUsed,
    params.search,
    params.discountType,
    params.target,
  ]);

  const columns: TableColumn<ICouponResponseData>[] = [
    {
      key: 'code',
      header: 'Code',
      render: (i) => (
        <span className={`uppercase ${i.isEmployee && 'text-primary'}`}>
          {i.code}
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Name',
      render: (i) => (
        <span className={`uppercase ${i.isEmployee && 'text-primary'}`}>
          {i.name}
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Coupon Type',
      render: (i) => (
        <span className={`uppercase ${i.isEmployee && 'text-primary'}`}>
          {i.type}
        </span>
      ),
    },
    {
      key: 'discountValue',
      header: 'Discount Value',
      render: (i) => (
        <span className={`uppercase ${i.isEmployee && 'text-primary'}`}>
          {i.discountValue}
        </span>
      ),
    },
    {
      key: 'maxDiscount',
      header: 'Max Discount',
      render: (i) => (
        <span className={`uppercase ${i.isEmployee && 'text-primary'}`}>
          {i.maxDiscount}
        </span>
      ),
    },
    {
      key: 'minimumValue',
      header: 'Minimum Value',
      render: (i) => (
        <span className={`uppercase ${i.isEmployee && 'text-primary'}`}>
          {i.minimumValue}
        </span>
      ),
    },
    {
      key: 'usageCount',
      header: 'Total Usage',
      render: (i) => (
        <span className={`uppercase ${i.isEmployee && 'text-primary'}`}>
          {i.usageCount}
        </span>
      ),
    },
    {
      key: 'isExpired',
      header: 'Status',
      render: (i) => {
        const notExpire = checkIsExpire(i.expirationDate);
        return (
          <span className={`${notExpire ? 'text-green-500' : 'text-red-500'}`}>
            {notExpire ? 'Active' : 'Expired'}
          </span>
        );
      },
    },
    {
      key: 'useStatus',
      header: 'Use Status',
      render: (i) => {
        const isUsed = i.usageCount > 0;
        return (
          <span className={`${isUsed ? 'text-green-500' : 'text-red-500'}`}>
            {isUsed ? 'Used' : 'Unused'}
          </span>
        );
      },
    },
    {
      key: 'couponType',
      header: 'Applied On',
      render: (i) => {
        if (i.brandIds?.length) {
          return 'Brand';
        }

        if (i.categoryIds?.length) {
          return 'Category';
        }

        return 'All Products';
      },
    },
  ];

  const actions: Action<ICouponResponseData>[] = [
    {
      text: 'View',
      onClick: (coupon) => navigate(`${RoutesLink.coupons}/${coupon.code}`),
    },
    {
      text: 'Edit',
      onClick: (coupon) => navigate(`${RoutesLink.addCoupon}/${coupon.code}`),
    },
    {
      text: 'Delete',
      onClick: (coupon) => {
        handleChangeState({ showDeleteModel: true, couponToDelete: coupon.id });
      },
    },
  ];

  const sortingOption = [
    { label: 'Newest', value: 'newest' },
    { label: 'Oldest', value: 'oldest' },
    { label: 'A To Z', value: 'az' },
    { label: 'Z To A', value: 'za' },
  ];

  const discountType = [
    { label: 'Percentage', value: 'percentage' },
    { label: 'Flat', value: 'flat' },
  ];

  const couponTarget = [
    { label: 'All', value: 'all' },
    { label: 'Brand', value: 'brand' },
    { label: 'Category', value: 'category' },
  ];

  // 🔹 Filter change handler
  const handleFilterChange = (filter: string) => {
    switch (filter) {
      case 'active':
        setParams({
          isActive: true,
          isExpired: '',
          isUsed: '',
        });
        break;

      case 'expired':
        setParams({
          isActive: '',
          isExpired: true,
          isUsed: '',
        });
        break;

      case 'used':
        setParams({
          isActive: '',
          isExpired: '',
          isUsed: true,
        });
        break;

      default:
        setParams({
          isActive: '',
          isExpired: '',
          isUsed: '',
        });
        break;
    }
  };
  const { selectedItems, handleSelectAll, handleSelectItem } =
    useSelectableTable(states.coupons);

  const handleChangePage = (page: number) => {
    setParams({ page });
  };

  const handleChangePageSize = (limit: number) => {
    setParams({
      page: 1,
      limit,
    });
  };

  const activeFilter = params.isActive
    ? 'active'
    : params.isExpired
      ? 'expired'
      : params.isUsed
        ? 'used'
        : '';

  return (
    <Layout>
      <PageHeading
        text="Coupons"
        button={{
          icon: <PlusSquare />,
          text: 'Create Coupon',
          onClick: () => {
            navigate(RoutesLink.addCoupon);
          },
        }}
      />
      <Card>
        <div className="flex items-center gap-3 justify-between flex-wrap">
          <div className="w-fit shrink-0">
            <CommonFilter
              activeFilter={activeFilter}
              onChange={(v) => handleFilterChange(v)}
              filters={couponFilterOption}
              showSearch={false}
              inputPlaceHolder="Search for Orders..."
              searchValue={params.search || ''}
              onSearch={(search) => {
                setParams({
                  search,
                });
              }}
            />
          </div>
          <div className="flex items-center gap-3">
            <div className="w-30">
              <FormField
                type="select"
                options={sortingOption}
                value={params.sort || ''}
                onChange={(e) =>
                  setParams({
                    sort: e.target.value,
                  })
                }
              />
            </div>
            <div className="w-30">
              <FormField
                type="select"
                placeholder="select discount type"
                options={discountType}
                value={params.discountType || ''}
                onChange={(e) =>
                  setParams({
                    discountType: e.target.value,
                  })
                }
              />
            </div>

            <div className="w-30">
              <FormField
                type="select"
                placeholder="select target"
                options={couponTarget}
                value={params.target || ''}
                onChange={(e) =>
                  setParams({
                    target: e.target.value,
                  })
                }
              />
            </div>
            {states.coupons.length > 0 && (
              <ExportOption
                APIPath={`${API_URL_ORDER_SERVICE}${API_ENDPOINT.coupons}/download-report`} // dateRange={dateRange[0]}
              />
            )}

            {/* <div>
            <DateRangePicker
              dateRange={dateRange}
              setDateRange={setDateRange}
            />
          </div> */}
          </div>
        </div>

        <CommonTable
          columns={columns}
          data={states.coupons}
          showCheckbox={false}
          selectedItems={selectedItems}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          getItemId={(item) => item.id}
          emptyMessage="No coupons found"
          loading={states.isLoading}
          actions={actions}
        />

        <Pagination
          paginationData={{
            page: params.page,
            limit: params.limit,
            total: states.pageInfo.total,
            totalPages: states.pageInfo.totalPages,
          }}
          isLoading={states.isLoading}
          onPageChange={(page) => handleChangePage(page)}
          onPageSizeChange={handleChangePageSize}
        />
      </Card>
      {states.showDeleteModel && (
        <ConfirmationModel
          type="coupon"
          onClose={() =>
            handleChangeState({ showDeleteModel: false, couponToDelete: null })
          }
          onConfirm={() => handleDeleteCoupon(states.couponToDelete as string)}
          show={states.showDeleteModel}
        />
      )}
    </Layout>
  );
};
export default CouponsList;
